import React, { useEffect } from 'react';
import { useSalleCompte, useSalleLien } from '../../../hooks/useSalle';

/**
 * Le QR code du formulaire de la salle, et le compteur qui monte.
 *
 * Posé sur la slide 3 — le moment où on demande à la salle de sortir son
 * téléphone. Voir docs/conferences/seminaire-QUESTIONS-SALLE.md.
 *
 * 24/09 — ET SUR LA SLIDE 1, PENDANT L'ACCUEIL (`invitation`). Retour de la
 * répétition : à la slide 3, on scanne pendant qu'Adrien parle, donc on
 * choisit entre écouter et répondre. Pendant que la salle s'installe, la
 * slide 1 reste à l'écran plusieurs minutes et personne n'écoute encore :
 * c'est le seul moment où remplir le formulaire ne fait rien rater. Le QR y
 * est surmonté d'une phrase qui dit quoi faire, et la slide 3 devient la
 * relance des retardataires — avec un compteur déjà lancé.
 *
 * 24/09, le soir — SUR LA SLIDE 1, IL EST PETIT. En grand, la pile phrase +
 * QR + adresse + code + compteur montait jusqu'au titre et s'affichait mal.
 * L'accueil se fait avec le QR PLEIN ÉCRAN, ouvert depuis le prompteur
 * (`QrSallePleinEcran`) ; celui-ci n'est plus que le rappel discret qui reste
 * quand Adrien le referme.
 *
 * ⚠️ LE LIEN EST RELU TANT QU'IL MANQUE. Il n'était lu qu'une fois, au
 * montage, et ça suffisait à la slide 3 : le backend était réveillé depuis
 * longtemps. À la slide 1, le diaporama s'ouvre parfois avant que Render ait
 * fini de se réveiller (jusqu'à 5 min) — le QR resterait sans le code de la
 * salle pendant tout l'accueil.
 *
 * ⚠️ L'URL N'EST PAS DANS LE DECK, ET C'EST TOUT L'INTÉRÊT. Elle porte le code
 * de la salle (`/q?c=…`), qui sert d'anti-spam ; les données des slides partent
 * dans le bundle public de Vercel, lisible par n'importe qui. Le QR demande
 * donc son contenu à `/api/salle/lien` avec le jeton admin du diaporama.
 *
 * ⚠️ REPLI SANS BACKEND. Si la route ne répond pas, on affiche quand même un QR
 * vers `/q` sur l'origine courante — c'est le bon domaine par construction,
 * puisque le diaporama y tourne. Il manquera le code : la page le demandera, et
 * la pastille ambre prévient Adrien qu'il devra le dicter. Un écran sans QR, à
 * cette seconde-là, coûterait toute l'interaction.
 *
 * ⚠️ Le compteur est relu toutes les 5 s (et non 15 comme au prompteur) : le
 * conducteur prévoit un silence pendant lequel la salle REGARDE le nombre
 * monter. À 15 s, il a l'air cassé.
 */
export default function QrSalle({ invitation = null }) {
    const { lien, erreur, relire } = useSalleLien();
    const { envois } = useSalleCompte(5000);

    // Toutes les 15 s tant que le lien n'est pas arrivé, puis plus rien.
    useEffect(() => {
        if (lien) return undefined;
        const t = setInterval(relire, 15000);
        return () => clearInterval(t);
    }, [lien, relire]);

    // Sans réponse du backend, l'origine courante reste juste : le diaporama
    // est servi par le même site que le formulaire.
    const replie = !lien;
    const url = lien?.url || `${window.location.origin}/q`;
    const courte = lien?.courte || `${window.location.host}/q`;
    const cote = invitation ? 'clamp(5.5rem, 17vh, 10.5rem)' : 'clamp(6rem, 20vh, 13rem)';

    return (
        <div className={`absolute ${invitation ? 'bottom-[4vh] right-8' : 'bottom-[12vh] right-12'} z-20 flex flex-col items-center gap-[1vh] animate-fade-in-scale stagger-3`}>
            {invitation && (
                <p
                    className="text-center font-semibold text-cyan-200 leading-snug max-w-[11rem]"
                    style={{ fontSize: 'clamp(0.65rem, min(0.95vw, 1.8vh), 1rem)' }}
                >
                    {invitation}
                </p>
            )}
            <div className="relative rounded-2xl bg-white p-[1.2vh] shadow-[0_0_50px_-12px_rgba(59,130,246,0.6)]">
                <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=0&data=${encodeURIComponent(url)}`}
                    alt="QR code du formulaire de questions"
                    style={{ width: cote, height: cote }}
                />
                {(replie || erreur) && (
                    <span
                        className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.9)]"
                        title="Le backend n'a pas répondu : QR sans le code de la salle, il faudra le dicter."
                    />
                )}
            </div>

            {/* Pas d'adresse ni de code sur la slide 1 : le QR plein écran de
                l'accueil les affiche, et le code se dicte depuis le prompteur. */}
            {!invitation && (
                <div className="text-center leading-tight">
                    <p
                        className="font-mono text-cyan-200/90"
                        style={{ fontSize: 'clamp(0.65rem, min(1.1vw, 2vh), 1.1rem)' }}
                    >
                        {courte}
                    </p>
                    {lien?.code && (
                        <p
                            className="font-mono font-bold tracking-[0.2em] text-white/90 uppercase"
                            style={{ fontSize: 'clamp(0.75rem, min(1.4vw, 2.6vh), 1.5rem)' }}
                        >
                            {lien.code}
                        </p>
                    )}
                </div>
            )}

            {/* Le compteur. Il n'apparaît qu'à la première contribution : un
                « 0 » projeté pendant que personne n'a encore envoyé est le
                meilleur moyen de faire croire que ça ne marche pas. */}
            {envois > 0 && (
                <div className="flex items-baseline gap-2">
                    <span
                        className="font-black tabular-nums bg-gradient-to-r from-blue-400 via-cyan-300 to-violet-400 bg-clip-text text-transparent"
                        style={{ fontSize: invitation ? 'clamp(1.1rem, min(2.4vw, 4.2vh), 2.2rem)' : 'clamp(1.5rem, min(4vw, 7vh), 3.5rem)' }}
                    >
                        {envois}
                    </span>
                    <span
                        className="text-slate-400/70 font-light"
                        style={{ fontSize: 'clamp(0.65rem, min(1.1vw, 2vh), 1.1rem)' }}
                    >
                        réponse{envois > 1 ? 's' : ''}
                    </span>
                </div>
            )}
        </div>
    );
}

/**
 * LE MÊME QR, EN GRAND, PAR-DESSUS N'IMPORTE QUELLE SLIDE.
 *
 * ⚠️ CE QU'IL RÉSOUT, ET C'EST UN PROBLÈME DE SCÈNE. Le QR n'existait qu'à la
 * slide 3. Or une salle ne répond pas toute en même temps : quelqu'un arrive
 * en retard, un autre n'a pas sorti son téléphone à temps, un troisième veut
 * poser sa question à la minute 35 et n'a plus la page ouverte. Revenir à la
 * slide 3 pour eux casserait le déroulé — et la barre de progression
 * annoncerait à toute la salle qu'on recule.
 *
 * Il s'ouvre donc depuis le PROMPTEUR, par le canal de synchronisation déjà
 * utilisé pour la navigation et F1 : le présentateur le fait apparaître, parle
 * par-dessus, et le referme. La slide dessous n'a pas bougé.
 *
 * Sans réponse du backend, on affiche quand même le QR vers `/q` sur l'origine
 * courante : c'est le bon domaine par construction, il manquera seulement le
 * code — voir le repli documenté sur `QrSalle`.
 */
export function QrSallePleinEcran({ onFermer }) {
    const { lien, erreur } = useSalleLien();
    const { envois } = useSalleCompte(5000);

    const replie = !lien;
    const url = lien?.url || `${window.location.origin}/q`;
    const courte = lien?.courte || `${window.location.host}/q`;

    return (
        <div
            className="fixed inset-0 z-[120] flex flex-col items-center justify-center gap-[3vh] bg-slate-950/97 backdrop-blur-xl animate-fade-in"
            onClick={onFermer}
            role="button"
            tabIndex={-1}
            title="Cliquer pour refermer"
        >
            <p
                className="font-mono uppercase tracking-[0.3em] text-cyan-300/80"
                style={{ fontSize: 'clamp(0.8rem, min(1.4vw, 2.6vh), 1.6rem)' }}
            >
                Vos questions
            </p>

            <div className="relative rounded-3xl bg-white p-[2vh] shadow-[0_0_120px_-20px_rgba(34,211,238,0.9)]">
                <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&data=${encodeURIComponent(url)}`}
                    alt="QR code du formulaire de questions"
                    style={{ width: 'min(46vh, 42vw)', height: 'min(46vh, 42vw)' }}
                />
                {(replie || erreur) && (
                    <span
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-amber-400 shadow-[0_0_16px_rgba(251,191,36,0.9)]"
                        title="Le backend n'a pas répondu : QR sans le code de la salle, il faudra le dicter."
                    />
                )}
            </div>

            <div className="text-center leading-tight">
                <p
                    className="font-mono text-cyan-200/90"
                    style={{ fontSize: 'clamp(0.9rem, min(1.8vw, 3.2vh), 2rem)' }}
                >
                    {courte}
                </p>
                {lien?.code && (
                    <p
                        className="font-mono font-bold tracking-[0.25em] text-white uppercase"
                        style={{ fontSize: 'clamp(1.1rem, min(2.4vw, 4.4vh), 2.8rem)' }}
                    >
                        {lien.code}
                    </p>
                )}
            </div>

            {envois > 0 && (
                <p
                    className="font-black tabular-nums bg-gradient-to-r from-blue-400 via-cyan-300 to-violet-400 bg-clip-text text-transparent"
                    style={{ fontSize: 'clamp(1.5rem, min(4vw, 7vh), 3.5rem)' }}
                >
                    {envois}
                    <span
                        className="ml-3 font-light text-slate-400/70"
                        style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.2rem)' }}
                    >
                        réponse{envois > 1 ? 's' : ''}
                    </span>
                </p>
            )}
        </div>
    );
}
